import React from 'react';
import { Outlet, BrowserRouter as Router, Routes, Route, Navigate, useParams, Link, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Layout from './components/Layout';
import CrewLayout from './components/CrewLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Today from './pages/Today';
import ManagerCheck from './pages/ManagerCheck';
import RoleSwitcher from './components/RoleSwitcher';
import Customers from './pages/Customers';
import TextPrefs from './pages/TextPrefs';
import Texts from './pages/Texts';
import CalendarPage from './pages/CalendarPage';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import RoutesPage from './pages/RoutesPage';
import ClientsMap from './pages/ClientsMap';
import Projects from './pages/Projects';
import Employees from './pages/Employees';
import Quotes from './pages/Quotes';
import QuoteVisit from './pages/QuoteVisit';
import BidderHome from './pages/BidderHome';
import InspectorHome from './pages/InspectorHome';
import LawnCare from './pages/LawnCare';
import PlanSheet from './pages/PlanSheet';
import Settings from './pages/Settings';
import Automations from './pages/Automations';
import WinterWork from './pages/WinterWork';
import PriceCheck from './pages/PriceCheck';
import FaqPage from './pages/FaqPage';
import Profit from './pages/Profit';
import MechanicHome from './pages/MechanicHome';
import LeadHome from './pages/LeadHome';
import CashFlow from './pages/CashFlow';
import Pay from './pages/Pay';
import Billing from './pages/Billing';
import InvoicePublic from './pages/InvoicePublic';
import CommercialAccounts from './pages/CommercialAccounts';
import CommercialAccount from './pages/CommercialAccount';
import CommercialToday from './pages/CommercialToday';
import SiteMapPage from './pages/SiteMapPage';
import JoinCrew from './pages/JoinCrew';
import ServiceReportPublic from './pages/ServiceReportPublic';
import BushCarePublic from './pages/BushCarePublic';
import CrewJobs from './pages/crew/CrewJobs';
import CrewJobDetail from './pages/crew/CrewJobDetail';
import CrewDashboard from './pages/crew/CrewDashboard';
import CrewSettings from './pages/crew/CrewSettings';
import QuotePublic from './pages/QuotePublic';
import VisibilityReport from './pages/VisibilityReport';
import Register from './pages/Register';
import Leads from './pages/Leads';
import Approves from './pages/Approves';
import EquipmentPage from './pages/Equipment';
import CustomerPortal from './pages/CustomerPortal';
import ReferralLanding from './pages/ReferralLanding';
import PlansPublic from './pages/PlansPublic';
import NotFound from './pages/NotFound';
import SalesLayout from './pages/sales/SalesLayout';
import SalesPlan from './pages/sales/SalesPlan';
import SalesStats from './pages/sales/SalesStats';

import { AuthProvider, useAuth } from './services/AuthContext';
import { isNoResponse } from './services/connection';
import ConnectionBanner from './components/ConnectionBanner';
import { rememberReturnTo } from './utils/returnTo';
import { ThemeProvider } from './services/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // One retry when the server answered with an error; two when it gave no
      // answer at all, since that is usually a blip that clears in seconds.
      // Whatever still fails shows in the ConnectionBanner with a Retry, and
      // is refetched by itself when the server is reachable again.
      retry: (count, err) => count < (isNoResponse(err) ? 2 : 1),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

function Spinner() {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <ConnectionBanner />
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600" />
      </div>
    </div>
  );
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  // 'viewer' is the read-only AI account — it browses the ADMIN app; the
  // server refuses every write it attempts, so full visibility is safe.
  if (user.role === 'sales') return <Navigate to="/sales" replace />;
  if (user.role === 'bidder') return <Navigate to="/bidder" replace />;
  if (user.role === 'inspector') return <Navigate to="/inspector" replace />;
  if (user.role === 'mechanic') return <Navigate to="/mechanic" replace />;
  if (user.role === 'manager') return <Navigate to="/lead" replace />;   // nothing admin side (owner, Sep 29 2026)
  if (user.role !== 'admin' && user.role !== 'viewer') return <Navigate to="/crew/dashboard" replace />;
  return <>{children}</>;
}

// The instant quote is one static page (public/get-quote.html) so the same file
// can be dropped on the marketing site; the app's old /quote link goes there.
function GoToInstantQuote() {
  React.useEffect(() => { window.location.replace(`/get-quote.html${window.location.search}`); }, []);
  return null;
}

// Bidder mode: a quote-only login. Can build and send quotes; reaches nothing
// else — no jobs, crew pay, profit or ledgers. Admin can use it too.
function RequireBidder({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'bidder' && user.role !== 'viewer') return <Navigate to="/crew/dashboard" replace />;
  return <>{children}</>;
}

// A stripped-down shell for the bidder: brand bar, log out, and the Quotes page.
// No admin nav — a bidder only ever sees quotes.
// Inspector mode: the week's random lawns and the checklist. Nothing else.
function RequireInspector({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!['admin', 'inspector', 'viewer', 'manager'].includes(user.role)) return <Navigate to="/crew/dashboard" replace />;
  return <>{children}</>;
}

// The shop: James's checklists, supply requests and the broken-gear queue.
// Manager and admin can walk in too (Ryan oversees it).
function RequireMechanic({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!['admin', 'manager', 'mechanic'].includes(user.role)) return <Navigate to="/crew/dashboard" replace />;
  return <>{children}</>;
}

// The crew lead's day — the manager hat's whole world. Nothing admin-side
// (owner, Sep 29 2026): no money pages, no customers, no billing.
function RequireLead({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!['admin', 'manager'].includes(user.role)) return <Navigate to="/crew/dashboard" replace />;
  return <>{children}</>;
}

function LeadLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const onHome = pathname === '/lead' || pathname === '/lead/';
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-800 text-white dark:bg-indigo-100 dark:text-gray-900 dark:border-b dark:border-indigo-300 px-4 py-3 flex items-center justify-between">
        {/* The name is the way home — tapping a checklist stranded him with no way back (owner, Sep 28 2026). */}
        <Link to="/lead" className="block">
          <p className="font-bold leading-tight">Keep Austin Mowed</p>
          <p className="text-xs text-indigo-200 dark:text-indigo-700">The day{user?.name ? ` · ${user.name}` : ''}</p>
        </Link>
        <div className="flex items-center gap-2">
          {!onHome && <Link to="/lead" className="text-sm font-semibold text-white dark:text-gray-900 border border-indigo-400 rounded-lg px-2.5 py-1">← The day</Link>}
          <RoleSwitcher tone="dark" compact /><button onClick={logout} className="text-sm text-indigo-200 hover:text-white dark:text-indigo-700 dark:hover:text-gray-900 font-medium">Log out</button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}

function MechanicLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-slate-800 text-white dark:bg-slate-100 dark:text-gray-900 dark:border-b dark:border-slate-300 px-4 py-3 flex items-center justify-between">
        <div>
          <p className="font-bold leading-tight">Keep Austin Mowed</p>
          <p className="text-xs text-slate-300 dark:text-slate-600">The shop{user?.name ? ` · ${user.name}` : ''}</p>
        </div>
        <div className="flex items-center gap-2"><RoleSwitcher tone="dark" compact /><button onClick={logout} className="text-sm text-slate-200 hover:text-white dark:text-slate-600 dark:hover:text-gray-900 font-medium">Log out</button></div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-5">
        <MechanicHome />
      </main>
    </div>
  );
}

function InspectorLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-emerald-700 text-white dark:bg-emerald-50 dark:text-gray-900 dark:border-b dark:border-emerald-200 px-4 py-3 flex items-center justify-between">
        <div>
          <p className="font-bold leading-tight">Keep Austin Mowed</p>
          <p className="text-xs text-emerald-200 dark:text-emerald-800">Inspections{user?.name ? ` · ${user.name}` : ''}</p>
        </div>
        <div className="flex items-center gap-2"><RoleSwitcher tone="dark" compact /><button onClick={logout} className="text-sm text-emerald-100 hover:text-white dark:text-emerald-800 dark:hover:text-gray-900 font-medium">Log out</button></div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-5">
        <InspectorHome />
      </main>
    </div>
  );
}

function BidderLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-brand-700 text-white dark:bg-plum dark:text-snow dark:border-b dark:border-snow/10 px-4 py-3 flex items-center justify-between">
        <div>
          <p className="font-bold leading-tight">Keep Austin Mowed</p>
          <p className="text-xs text-brand-200 dark:text-snow/70">Quotes{user?.name ? ` · ${user.name}` : ''}</p>
        </div>
        <div className="flex items-center gap-2"><RoleSwitcher tone="dark" compact /><button onClick={logout} className="text-sm text-brand-100 hover:text-white dark:text-snow/80 dark:hover:text-snow font-medium">Log out</button></div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-5">
        <BidderHome />
      </main>
    </div>
  );
}

// Sales mode: the owner (or a salesperson) knocking doors. Admin can use it too.
function RequireSales({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'sales' && user.role !== 'viewer' && user.role !== 'manager') return <Navigate to="/crew/dashboard" replace />;
  return <>{children}</>;
}

function RequireCrew({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) return <Spinner />;
  if (!user) {
    // The open-shift text links to /crew/jobs?tab=open: come back here after the PIN (utils/returnTo — crew pages only).
    rememberReturnTo(pathname + search);
    return <Navigate to="/login" replace />;
  }
  // The Mechanic hat has no Employee side: /api/jobs, the clock and crew pay
  // all refuse it, so the crew home came up on old numbers with every call
  // behind it failing — "Reading Crew Pay…" forever. He gets here by the
  // phone's Back after "Open the shop list" (the owner, Oct 6 2026: the shop
  // weekly is "a part of the mechanic employee tag"), which switches hats.
  // Send that hat to its own home, the way RequireMechanic sends every other
  // hat out of the shop. Employee is one tap away on the hat switcher there.
  // (Admin and the view-only logins stay: the crew app works for them.)
  if (user.role === 'mechanic') return <Navigate to="/mechanic" replace />;
  return <>{children}</>;
}

/** /customers/:id → the Customers page with that card open (the page opens cards by ?profile=). */
function CustomerById() {
  const { id } = useParams();
  return <Navigate to={/^\d+$/.test(String(id)) ? `/customers?profile=${id}` : '/customers'} replace />;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'sales') return <Navigate to="/sales" replace />;
  if (user.role === 'bidder') return <Navigate to="/bidder" replace />;
  if (user.role === 'inspector') return <Navigate to="/inspector" replace />;
  if (user.role === 'mechanic') return <Navigate to="/mechanic" replace />;
  if (user.role === 'manager') return <Navigate to="/lead" replace />;
  if (user.role === 'crew' || user.role === 'crew_viewer') return <Navigate to="/crew/dashboard" replace />;
  // The owner's day starts in the field (his own stops); the office is a switch away.
  if (user.choices?.field?.some(f => f.home === '/field')) return <Navigate to="/field" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <ThemeProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/q/:token" element={<QuotePublic />} />
            {/* Public instant quote — deliberately /quote (singular); the office Bids page is /quotes. */}
            <Route path="/quote" element={<GoToInstantQuote />} />
            <Route path="/invoice/:token" element={<InvoicePublic />} />
            {/* The report a commercial site's manager opens from the email after a visit */}
            <Route path="/service-report/:token" element={<ServiceReportPublic />} />
            {/* How to water a new planting, and the care plan the bid offered (services/bushFollowUp) */}
            <Route path="/bush-care/:token" element={<BushCarePublic />} />
            {/* The customer's own page — a private link, no password */}
            <Route path="/my/:token" element={<CustomerPortal />} />
            {/* Where a customer's shared referral link lands */}
            <Route path="/r/:code" element={<ReferralLanding />} />
            {/* A new crew member makes their own PIN from the office's link */}
            <Route path="/join/:token" element={<JoinCrew />} />
            <Route path="/plans" element={<PlansPublic />} />
            <Route path="/" element={<RootRedirect />} />

            {/* Admin */}
            <Route path="/" element={<RequireAdmin><Layout /></RequireAdmin>}>
              <Route path="dashboard"  element={<Dashboard />} />
              {/* The one morning page (owner, Nov 2 2026): Needs Attention, Field, My Day and the manager check,
                  top to bottom. The four old pages still answer at their own addresses — each is this page
                  scrolled to its block, so every bookmark and the hat switcher's homes keep working. */}
              <Route path="today"      element={<Today />} />
              <Route path="field"      element={<Today focus="field" />} />
              <Route path="my-day"     element={<Today focus="my-day" />} />
              <Route path="check"      element={<Today focus="check" />} />
              <Route path="attention"  element={<Today focus="attention" />} />
              {/* The manager's proposals. The API has been live since the manager hat shipped; this page was never
                  built, so /approves and /change-requests were "Page not found" and the queue was invisible. */}
              <Route path="approves"   element={<Approves />} />
              <Route path="change-requests" element={<Navigate to="/approves" replace />} />
              <Route path="approvals"  element={<Navigate to="/approves" replace />} />
              <Route path="customers"  element={<Customers />} />
              {/* /customers/123 is how people expect a customer link to look: it opens that card. */}
              <Route path="customers/texts" element={<TextPrefs />} />
              <Route path="customers/:id" element={<CustomerById />} />
              {/* Every text on the business number, read and answered in the app (owner, Oct 14 2026). */}
              <Route path="texts"        element={<Texts />} />
              <Route path="texts/:phone" element={<Texts />} />
              <Route path="calendar"   element={<CalendarPage />} />
              <Route path="bids"       element={<BidderHome embedded />} />
              <Route path="jobs"       element={<Jobs />} />
              {/* Before jobs/:id, or "new" is read as a job id and the detail
                  page goes looking for job #new. */}
              <Route path="jobs/new"   element={<Navigate to="/jobs?new=1" replace />} />
              <Route path="jobs/:id"   element={<JobDetail />} />
              <Route path="quote-visits/:id" element={<QuoteVisit />} />
              <Route path="employees"  element={<Employees />} />
              <Route path="routes"     element={<RoutesPage />} />
              <Route path="who-sees"   element={<VisibilityReport />} />
              <Route path="map"        element={<ClientsMap />} />
              <Route path="billing"    element={<Billing />} />
              <Route path="commercial" element={<CommercialAccounts />} />
              <Route path="commercial/today" element={<CommercialToday />} />
              <Route path="commercial/:id" element={<CommercialAccount />} />
              {/* The site map (owner, Oct 2 2026): what gets done where on a lot, and the locks with their codes. */}
              <Route path="site-map/:propertyId" element={<SiteMapPage />} />
              <Route path="projects"   element={<Projects />} />
              <Route path="quotes"     element={<Quotes />} />
              {/* Programs and add-ons as one page (Nov 2 2026). The two old addresses still open it, on their tab. */}
              <Route path="lawn-care"  element={<LawnCare />} />
              <Route path="treatments" element={<LawnCare tab="programs" />} />
              <Route path="treatments/sheet" element={<PlanSheet />} />
              <Route path="mow-addons"  element={<LawnCare tab="addons" />} />
              <Route path="leads"      element={<Leads />} />
              <Route path="equipment"  element={<EquipmentPage />} />
              <Route path="profit"     element={<Profit />} />
              <Route path="cashflow"   element={<CashFlow />} />
              <Route path="pay"        element={<Pay />} />
              <Route path="settings"   element={<Settings />} />
              <Route path="automations" element={<Automations />} />
              <Route path="winter"     element={<WinterWork />} />
              <Route path="price-check" element={<PriceCheck />} />
              <Route path="faq"        element={<FaqPage />} />
              <Route path="*"          element={<NotFound />} />
            </Route>

            {/* Every other admin page lives at the root, so the profit sheet does
                too — this keeps the /admin/profit URL working either way. */}
            <Route path="/admin/profit" element={<Navigate to="/profit" replace />} />

            {/* Sales mode */}
            <Route path="/sales" element={<RequireSales><SalesLayout /></RequireSales>}>
              <Route index          element={<SalesPlan />} />
              <Route path="stats"   element={<SalesStats />} />
              <Route path="*"       element={<Navigate to="/sales" replace />} />
            </Route>

            {/* Bidder mode — quotes only */}
            <Route path="/bidder" element={<RequireBidder><BidderLayout /></RequireBidder>} />
            <Route path="/inspector" element={<RequireInspector><InspectorLayout /></RequireInspector>} />
            <Route path="/mechanic" element={<RequireMechanic><MechanicLayout /></RequireMechanic>} />
            <Route path="/lead" element={<RequireLead><LeadLayout /></RequireLead>}>
              <Route index element={<LeadHome />} />
              <Route path="check" element={<ManagerCheck />} />
            </Route>

            {/* Crew */}
            <Route path="/crew" element={<RequireCrew><CrewLayout /></RequireCrew>}>
              <Route index             element={<Navigate to="/crew/dashboard" replace />} />
              <Route path="dashboard"  element={<CrewDashboard />} />
              <Route path="jobs"       element={<CrewJobs />} />
              <Route path="jobs/:id"   element={<CrewJobDetail />} />
              <Route path="settings"   element={<CrewSettings />} />
              <Route path="profile"    element={<Navigate to="/crew/settings" replace />} />
              {/* Availability lives on the crew settings page. */}
              <Route path="availability" element={<Navigate to="/crew/settings" replace />} />
              {/* Anything else is a dead link — say so rather than dumping the
                  crew on the dashboard as if it had worked. */}
              <Route path="*"          element={<NotFound home="/crew/dashboard" homeLabel="Back to my day" />} />
            </Route>
          </Routes>
          <ToastContainer position="top-right" autoClose={4000} />
          </ThemeProvider>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
